import * as React from "react";
import { GridColumns, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { ActionCreatorWithPayload } from "@reduxjs/toolkit";
import {
    CancelProductSaleParticipantRequest,
    Participant,
    ProductSaleParticipantSendInvitationRequest,
    ResendParticipantInvitationsRequest,
    SetParticipantAttendancePayload,
    SetSessionAttendancePayload,
} from "../../../../services/types/enterpriseParticipantApiTypes";
import { AlertColor, Switch, Typography } from "@mui/material";
import {
    useResendParticipantInvitationsMutation,
    useSetParticipantAttendanceMutation,
    useSetSessionAttendanceMutation,
    useSendProductSaleParticipantsInvitationMutation,
    useCancelProductSaleParticipantsMutation,
    useGetSessionParticipantsAssessmentStatusQuery,
} from "../../../../services/cclTokenedEnterpriseParticipantApi";
import {
    AccessEventRegistrationDetails,
    AccessEventSessionDetails,
} from "../../../../services/types/accessEventTypes";
import { addCustomDataGridFilters } from "../../../../components/common/customDataGridFilters/customDataGridFilters";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import CclPersistentDataGrid, {
    CclPersistantDataGridMiscButton,
} from "../../../../components/common/cclPersistentDataGrid/cclPersistentDataGrid";
import { useSelector } from "react-redux";
import CclCommonLink from "../../../../components/common/cclCommonLink";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { AP_SURVEY_PLATFORM_KEYS, SessionDetailType } from "../../../../services/types/sessionDetailTypes";
import RegisterParticipantDrawer from "./registerParticipantDrawer";
import RegisterPartnerParticipantDrawer from "./registerPartnerParticipantDrawer";
import TransferParticipantDrawer from "./transferParticipantDrawer";
import ParticipantContactDrawer from "../assessments/scoringStatus/components/participantContactDrawer/participantContactDrawer";
import ParticipantKeyDatesDrawer from "../assessments/scoringStatus/components/participantKeyDatesDrawer/participantKeyDatesDrawer";
import {
    eGroupIds,
    UserClaimsService,
} from "../../../../services/currentUserService/currentUserService";
import InventoryDrawer from "./inventoryDrawer";
import { updateSessionsParticipantDatesGridState } from "../../../../app/slices/sessionParticipantDatesTableStateSlice";
import { useAreaPreference } from "../../../../hooks/useAreaPreference";
import { InventoryDetail } from "../../../../services/types/assessmentAPITypes";

export interface SessionParticipantDatesListDataGridProps {
    participants: Participant[] | undefined;
    showAttendance: boolean;
    session: SessionDetailType;
    inventory?: InventoryDetail;
    inventoryIsFetching?: boolean;
    onParticipantDatesSaved?: () => void;
    participantsRefetching?: boolean;
    /** Hides the toolbar function buttons and row selection; the Show Only Confirmed toggle stays. */
    hideActions?: boolean;
    /** Area preference key (under "sessionDetail") for the Show Only Confirmed toggle; defaults to the Registrations one. */
    showOnlyConfirmedPreferenceKey?: string;
    autoHeight?: boolean;
    /** Names the export `<session code>-<suffix>.csv`, even when there are no rows. */
    exportNameSuffix?: string;
    /** Overrides the Redux table state; defaults to the Registrations (Dates) slice. */
    tableStateSelector?: { TableState: string };
    tableStateActionCreator?: ActionCreatorWithPayload<any, any>;
}

export const SessionParticipantDatesListDataGrid: React.FC<
    SessionParticipantDatesListDataGridProps
> = (props) => {
    const claimsService = new UserClaimsService();

    // state
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");
    const [showOnlyConfirmed, setShowOnlyConfirmed] = useAreaPreference<boolean>(
        "sessionDetail",
        props.showOnlyConfirmedPreferenceKey ?? "registrations.showOnlyConfirmed",
        false,
    );
    const [selectedRows, setSelectedRows] = React.useState<Participant[]>([]);
    const [allAttended, setAllAttended] = React.useState<boolean>(false);
    const [sessionCode, setSessionCode] = React.useState<string>("");
    const [openRegisterDrawer, setOpenRegisterDrawer] = React.useState<boolean>(false);
    const [openTransferDrawer, setOpenTransferDrawer] = React.useState<boolean>(false);
    const [selectedParticipant, setSelectedParticipant] = React.useState<Participant | null>(null);
    const [openContactDrawer, setOpenContactDrawer] = React.useState<boolean>(false);
    const [openDatesDrawer, setOpenDatesDrawer] = React.useState<boolean>(false);
    const [openInventoryDrawer, setOpenInventoryDrawer] = React.useState<boolean>(false);
    const [sendingInvitations, setSendingInvitations] = React.useState<boolean>(false);
    const [pendingEsiKeys, setPendingEsiKeys] = React.useState<Set<number>>(new Set());
    const [viewableParticipants, setViewableParticipants] = React.useState<Participant[]>(
        props.participants || [],
    );

    // hooks
    const [setPaxAttendance] = useSetParticipantAttendanceMutation();
    const [setSessionAttendance, { isLoading: setSessionIsLoading }] =
        useSetSessionAttendanceMutation();
    const [sendProductSaleInvitations] = useSendProductSaleParticipantsInvitationMutation();
    const [resendParticipantInvitations] = useResendParticipantInvitationsMutation();
    const [cancelParticipant, { isLoading: cancelParticipantInProgress }] = useCancelProductSaleParticipantsMutation();

    const { logEvent } = useLogAccessEvent();

    const { data: assessmentStatusData } = useGetSessionParticipantsAssessmentStatusQuery(
        props.session.sessionKey,
        {
            skip: !props.session.sessionKey,
        },
    );

    const canEditAttendance = claimsService.IsUserInGroups([
        eGroupIds.eCCLAdmin,
        eGroupIds.eCCLSuperAdmin,
    ]);

    const isNwaAdmin = claimsService.IsUserNwaAdmin();
    const isLicensee = props.session.managingLocationKey === 6;

    React.useEffect(() => {
        if (props.session == null) return;
        setSessionCode(props.session.sessionCode);
    }, [props.session]);

    React.useEffect(() => {
        if (props.participants != null && props.participants.length > 0) {
            const numAttended = props.participants.filter((p) => p.attended).length;
            setAllAttended(numAttended === props.participants.length);
        }
    }, [props.participants]);

    // derived (not effect-driven) so the name is still correct when the participant list is empty;
    // only the card (which sets a suffix) falls back to the session code, the main grid keeps its original name
    const exportCode =
        props.participants?.[0]?.projectCode ??
        (props.exportNameSuffix ? props.session?.sessionCode : undefined);
    const exportFileName = `${exportCode ? `${exportCode}-` : ""}${
        props.exportNameSuffix ?? "ParticipantDatesList"
    }.csv`;

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    const handleAttendanceChange = (pax: Participant) => {
        const newAttended = !pax.attended;
        const payload: SetParticipantAttendancePayload = {
            esiKey: pax.esiKey,
            esKey: +props.session.sessionKey,
            payload: {
                attended: newAttended,
            },
        };

        setViewableParticipants((prev) =>
            prev.map((p) => (p.esiKey === pax.esiKey ? { ...p, attended: newAttended } : p)),
        );
        setPendingEsiKeys((prev) => new Set(prev).add(pax.esiKey));

        setPaxAttendance(payload)
            .unwrap()
            .then(() => {
                const evtData: AccessEventSessionDetails = {
                    sessionID: props.session.sessionKey?.toString() ?? "",
                    projectCode: sessionCode,
                };

                logEvent("ParticipantAttendanceSaved", evtData);
                ShowSnackbarElement("Participant Attendance Updated", "success");
            })
            .catch(() => {
                setViewableParticipants((prev) =>
                    prev.map((p) =>
                        p.esiKey === pax.esiKey ? { ...p, attended: pax.attended } : p,
                    ),
                );
                ShowSnackbarElement("Participant Attendance Update Failed", "error");
            })
            .finally(() => {
                setPendingEsiKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(pax.esiKey);
                    return next;
                });
            });
    };

    const handleAttendanceChangeForAll = () => {
        const payload: SetSessionAttendancePayload = {
            esKey: +props.session.sessionKey,
            payload: {
                attended: true,
            },
        };
        setSessionAttendance(payload)
            .unwrap()
            .then(() => {
                const evtData: AccessEventSessionDetails = {
                    sessionID: props.session.sessionKey?.toString() ?? "",
                    projectCode: sessionCode,
                };

                logEvent("ParticipantAttendanceSaved", evtData);
                ShowSnackbarElement("Participant Attendance Updated", "success");
            })
            .catch(() => {
                ShowSnackbarElement("Participant Attendance Update Failed", "error");
            });
    };

    function createDateValue(dtStr: string | null): Date | null {
        if (dtStr === null) return null;

        const finalDtStr = dtStr.endsWith("Z") ? dtStr.slice(0, -1) : dtStr;
        return new Date(finalDtStr);
    }

    function getFullName(params: any) {
        return `${params.row.firstName || ""} ${params.row.lastName || ""}`;
    }

    function renderDateCell(dt: Date | null) {
        if (dt !== null) {
            if (!(dt.getFullYear() === 9999 && dt.getMonth() === 11 && dt.getDate() === 31)) {
                return <span>{dt.toLocaleDateString()}</span>;
            }
        }
        return <span></span>;
    }

    const COLUMNS = [
        {
            field: "attendance",
            type: "string",
            headerName: "Attendance",
            renderCell: (params: GridRenderCellParams) =>
                !canEditAttendance ? (
                    <Typography variant={"body2"}>
                        {params.row["attended"] ? "Attended" : "Not Attended"}
                    </Typography>
                ) : (
                    <Switch
                        size="small"
                        color="primary"
                        checked={params.row["attended"]}
                        onChange={() => handleAttendanceChange(params.row)}
                        inputProps={{ "aria-label": "mark-attendance" }}
                        disabled={
                            params.row["registrationStatus"] !== "Confirmed" ||
                            pendingEsiKeys.has(params.row["esiKey"])
                        }
                    />
                ),
            sortable: false,
            disableExport: true,
            minWidth: 95,
            flex: 0.5,
        },
        {
            field: "registrationStatus",
            headerName: "Status",
            minWidth: 100,
            flex: 0.7,
            renderCell: (params: GridRenderCellParams) =>
                params.value !== "Confirmed" ? (
                    <>
                        {params.value}
                        <ErrorOutlineIcon color="error" fontSize="small" sx={{ pl: 1 }} />
                    </>
                ) : (
                    `${params.value}`
                ),
        },
        {
            field: "name",
            headerName: "Participant",
            type: "string",
            minWidth: 180,
            flex: 1.5,
            valueGetter: getFullName,
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`/registration/${params.row.esiKey}`}
                />
            ),
        },
        {
            field: "firstName",
            headerName: "First Name",
            hide: true,
        },
        {
            field: "lastName",
            headerName: "Last Name",
            hide: true,
        },
        {
            field: "emailAddress",
            headerName: "Email",
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`mailto:${params.value}`}
                    type={"email"}
                />
            ),
            minWidth: 200,
            flex: 1,
        },
        {
            field: "registrationDate",
            headerName: "Registered",
            valueGetter: (params: any) => createDateValue(params.row.registrationDate),
            type: "date",
            renderCell: (params: GridRenderCellParams) => {
                return renderDateCell(params.value);
            },
            minWidth: 100,
            flex: 0.5,
        },
        {
            field: "facilitatorName",
            headerName: "Facilitator",
            type: "string",
            minWidth: 180,
            flex: 1.25,
        },
        {
            field: "preferredLanguage",
            headerName: "Language",
            minWidth: 100,
            flex: 0.7,
        },
        {
            field: "scheduledPreworkInvitation",
            headerName: "Invitation (Scheduled)",
            valueGetter: (params: any) => createDateValue(params.row.scheduledPreworkInvitation),
            type: "date",
            renderCell: (params: GridRenderCellParams) => {
                return renderDateCell(params.value);
            },
            minWidth: 120,
            flex: 0.5,
        },
        {
            field: "actualPreworkInvitation",
            headerName: "Invitation (Actual)",
            valueGetter: (params: any) => createDateValue(params.row.actualPreworkInvitation),
            type: "date",
            renderCell: (params: GridRenderCellParams) => {
                return renderDateCell(params.value);
            },
            minWidth: 110,
            flex: 0.5,
        },
        {
            field: "deadline",
            headerName: "Assessment Completion",
            valueGetter: (params: any) => createDateValue(params.row.deadline),
            type: "date",
            renderCell: (params: GridRenderCellParams) => {
                return renderDateCell(params.value);
            },
            minWidth: 140,
            flex: 0.5,
        },
        {
            field: "accessExpiration",
            headerName: "Access Expiration",
            valueGetter: (params: any) => createDateValue(params.row.accessExpiration),
            type: "date",
            renderCell: (params: GridRenderCellParams) => {
                return renderDateCell(params.value);
            },
            minWidth: 110,
            flex: 0.5,
        },
        {
            field: "orderId",
            headerName: "Order #",
            type: "string",
            minWidth: 110,
            flex: 0.7,
            renderCell: (params: GridRenderCellParams) =>
                params.value ? (
                    claimsService.IsUserInGroups([
                        eGroupIds.eCCLSuperAdmin,
                        eGroupIds.eClientAdmin,
                    ]) && props.session.assessmentAdminGroupId ? (
                        <CclCommonLink
                            text={params.value}
                            keyVal={params.value}
                            to={`/inventory/${params.value}?groupId=${props.session.assessmentAdminGroupId}`}
                        />
                    ) : (
                        <span>{params.value}</span>
                    )
                ) : null,
        },
    ];

    const datesTableState = useSelector((state: any) => state.sessionParticipantDatesTableState);
    const stateSelector = props.tableStateSelector ?? datesTableState;
    const visibilityModel = {
        attendance: props.showAttendance,
        registrationStatus: true,
        name: true,
        firstName: false,
        lastName: false,
        emailAddress: true,
        registrationDate: true,
        preferredLanguage: true,
        scheduledPreworkInvitation: true,
        actualPreworkInvitation: true,
        deadline: true,
        accessExpiration: true,
        orderId: props.session.typeKey === 7,
        facilitatorName: props.session.typeKey === 7,
    };

    // visibility model setting wasn't working as expected so for now eliminating the column with a filter if needed
    const actualColumns = props.showAttendance
        ? COLUMNS
        : COLUMNS.filter((c) => c.field !== "attendance");

    const handleSelectedRows = (rows: any[] | undefined) => {
        setSelectedRows(rows || []);
    };

    React.useEffect(() => {
        if (props.participants && props.participants.length > 0) {
            if (showOnlyConfirmed) {
                setViewableParticipants(
                    props.participants.filter((p) => p.registrationStatus === "Confirmed"),
                );
            } else {
                setViewableParticipants(props.participants);
            }
        } else {
            setViewableParticipants([]);
        }
    }, [props.participants, showOnlyConfirmed]);

    const handleTransferParticipant = (row: Participant) => {
        setSelectedParticipant(row);
        setOpenTransferDrawer(true);
    };

    const handleCancelParticipant = (row: Participant) => {
        var payload: CancelProductSaleParticipantRequest = {
            esKey: row.esKey,
            esiKeys: [row.esiKey],
        };
        cancelParticipant(payload)
            .unwrap()
            .then((response) => {
                if (
                    response.canceledParticipantResults.length > 0 &&
                    response.canceledParticipantResults[0].success
                ) {
                    ShowSnackbarElement("Participant Canceled", "success");
                    let eventData: AccessEventRegistrationDetails = {
                        esiKey: row.esiKey.toString(),
                        imKey: row.imKey.toString(),
                        email: row.emailAddress,
                    };
                    logEvent("ParticipantCancelled", eventData);
                } else {
                    ShowSnackbarElement("Cancel Participant Failed", "error");
                }
            })
            .catch(() => {
                ShowSnackbarElement("Cancel Participant Failed", "error");
            });
    };

    const handleContactOpen = (row: Participant) => {
        setSelectedParticipant(row);
        setOpenContactDrawer(true);
    };

    const handleDatesOpen = (row: Participant) => {
        setSelectedParticipant(row);
        setOpenDatesDrawer(true);
    };

    const sendInvitations = () => {
        const esiKeys = selectedRows.map((r) => r.esiKey);
        setSendingInvitations(true);
        if (isLicensee) {
            const payload: ResendParticipantInvitationsRequest = {
                participantEsiKeys: esiKeys,
                esKey: props.session.sessionKey,
            };
            resendParticipantInvitations(payload)
                .unwrap()
                .then(() => {
                    setSendingInvitations(false);
                    ShowSnackbarElement("Invitation(s) Sent", "success");
                    selectedRows.forEach((r) => {
                        const eventData: AccessEventRegistrationDetails = {
                            esiKey: r.esiKey.toString(),
                            imKey: r.imKey.toString(),
                            email: r.emailAddress,
                        };
                        logEvent("ParticipantInvitationsResent", eventData);
                    });
                })
                .catch(() => {
                    setSendingInvitations(false);
                    ShowSnackbarElement("Send Invitation(s) Failed", "error");
                });
        } else if (props.session.typeKey === 7) {
            const payload: ProductSaleParticipantSendInvitationRequest = {
                esKey: props.session.sessionKey,
                esiKeys: esiKeys,
            };
            sendProductSaleInvitations(payload)
                .unwrap()
                .then((response) => {
                    setSendingInvitations(false);
                    ShowSnackbarElement("Invitation(s) Sent", "success");
                    response.sendInvitationResults.forEach((result) => {
                        if (result.success) {
                            const p = props.participants?.find((p) => p.esiKey === result.esiKey);
                            if (p) {
                                const eventData: AccessEventRegistrationDetails = {
                                    esiKey: p.esiKey.toString(),
                                    imKey: p.imKey.toString(),
                                    email: p.emailAddress,
                                };
                                logEvent(
                                    result.isResend
                                        ? "ParticipantInvitationsResent"
                                        : "ParticipantInvitationSent",
                                    eventData,
                                );
                            }
                        }
                    });
                })
                .catch(() => {
                    setSendingInvitations(false);
                    ShowSnackbarElement("Send Invitation(s) Failed", "error");
                });
        }
        // maybe later we'll add other session types here
    };

    const checkCancelled = () => {
        // Must have exactly one participant selected with Confirmed status
        if (selectedRows.length !== 1 || selectedRows[0]?.registrationStatus !== "Confirmed") {
            return true;
        }

        // Check assessment status conditions
        const participant = selectedRows[0];
        const participantAssessmentStatus = assessmentStatusData?.participants?.find(
            (p) => p.esiKey === participant.esiKey,
        );

        if (participantAssessmentStatus) {
            // Check all instruments for blocking conditions
            const allInstruments = [
                ...(participantAssessmentStatus.instruments || []),
                ...(participantAssessmentStatus.additionalInstruments || []),
            ];

            for (const instrument of allInstruments) {
                const ratersInvited =
                    instrument.raters?.some((rater) => rater.surveysSent > 0) ?? false;

                // Condition 1: Not started (status 1) but has assigned raters
                if (instrument.selfAssessmentStatusTypeId === 1 && ratersInvited) {
                    return true; // Disable button
                }

                // Condition 2: Assessment is In-Progress (status 2, 3, 4, 5)
                if (
                    instrument.selfAssessmentStatusTypeId >= 2 &&
                    instrument.selfAssessmentStatusTypeId <= 5
                ) {
                    return true; // Disable button
                }

                // Condition 3: Assessment is Scored (scoringStatus 2 = scored)
                if (instrument.scoringStatus === 2) {
                    return true; // Disable button
                }
            }
        }

        return false; // Enable button
    };

    const buttons: CclPersistantDataGridMiscButton[] = [];

    if (props.showAttendance && canEditAttendance) {
        buttons.push({
            id: "session-mark-all-attended",
            caption: "Mark All Attended",
            onClick: handleAttendanceChangeForAll,
            returnSelected: true,
            disable: allAttended,
            restricted: false,
            loadingBtn: true,
            isLoading: setSessionIsLoading,
        });
    }

    const showProductSaleButtons = props.session.typeKey === 7 && !isNwaAdmin;
    const showLicenseeAdminButtons =
        isLicensee &&
        claimsService.IsUserInGroups([
            eGroupIds.eCCLAdmin,
            eGroupIds.eCCLSuperAdmin,
            eGroupIds.eNWAAdmin,
        ]);
    const isApSurveyPlatform = AP_SURVEY_PLATFORM_KEYS.includes(props.session.assessmentPlatformKey);

    if (showProductSaleButtons) {
        buttons.push({
            id: "view-inventory",
            caption: "View Inventory",
            onClick: () => {
                setOpenInventoryDrawer(true);
            },
            returnSelected: false,
            disable: props.inventory == null,
            restricted: false,
            loadingBtn: true,
            isLoading: props.inventoryIsFetching ?? false,
        });
        if (isApSurveyPlatform) {
            buttons.push({
                id: "register-participant",
                caption: "Register Participant",
                onClick: () => {
                    setOpenRegisterDrawer(true);
                },
                returnSelected: false,
                disable: props.inventory == null,
                restricted: false,
                loadingBtn: true,
                isLoading: props.inventoryIsFetching ?? false,
            });
        }
    }

    if (showLicenseeAdminButtons && isApSurveyPlatform) {
        buttons.push({
            id: "register-participant",
            caption: "Register Participant",
            onClick: () => {
                setOpenRegisterDrawer(true);
            },
            returnSelected: false,
            disable: false,
            restricted: false,
        });
    }

    if (showProductSaleButtons && props.session.hostingPlatformKey !== 10 && isApSurveyPlatform) {
        buttons.push({
            id: "send-invites",
            caption: "Send Invitation(s)",
            onClick: () => {
                sendInvitations();
            },
            returnSelected: false,
            loadingBtn: true,
            isLoading: sendingInvitations,
            disable: selectedRows.length === 0,
            restricted: false,
        });
    }

    buttons.push({
        id: "view-contact-details",
        caption: "View Contact Details",
        onClick: () => {
            handleContactOpen(selectedRows[0]);
        },
        returnSelected: false,
        disable: selectedRows.length !== 1,
        restricted: false,
    });

    if (isApSurveyPlatform) {
        buttons.push({
            id: "edit-participant-dates",
            caption: "Edit Participant Dates",
            onClick: () => {
                handleDatesOpen(selectedRows[0]);
            },
            returnSelected: false,
            disable: selectedRows.length !== 1,
            restricted: false,
        });
    }

    if ((showProductSaleButtons || showLicenseeAdminButtons) && isApSurveyPlatform) {
        buttons.push({
            id: "transfer-participant",
            caption: "Transfer Participant",
            onClick: () => {
                handleTransferParticipant(selectedRows[0]);
            },
            returnSelected: false,
            disable: selectedRows.length !== 1,
            restricted: false,
        });
        buttons.push({
            id: "cancel-participant",
            caption: "Cancel Participant",
            onClick: () => {
                handleCancelParticipant(selectedRows[0]);
            },
            returnSelected: false,
            disable: checkCancelled(),
            restricted: false,
            loadingBtn: true,
            isLoading: cancelParticipantInProgress,
        });
    }

    const showOnlyConfirmedButton: CclPersistantDataGridMiscButton = {
        // distinct id in card mode so the two grids never share a DOM id when both are mounted
        id: props.hideActions ? "uninvited-show-only-confirmed-pax" : "show-only-confirmed-pax",
        type: "switch",
        caption: "Show Only Confirmed",
        onToggled: (checked: boolean) => setShowOnlyConfirmed(checked),
        restricted: false,
        returnSelected: false,
        checkedState: showOnlyConfirmed,
        disable: false,
        onClick: function (rows?: any[]) {
            return;
        },
    };
    buttons.push(showOnlyConfirmedButton);

    return (
        <React.Fragment>
            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    severity={snackbarSeverity}
                    message={snackbarMessage}
                    onClose={() => setShowSnackbar(false)}
                />
            ) : null}

            {openRegisterDrawer && !isLicensee ? (
                <RegisterParticipantDrawer
                    open={true}
                    onClose={() => setOpenRegisterDrawer(false)}
                    session={props.session}
                    showSnackbar={(message: string, severity: AlertColor) => {
                        setSnackbarMessage(message);
                        setSnackbarSeverity(severity);
                        setShowSnackbar(true);
                    }}
                    hasInventory={props.inventory != null && props.inventory.projectedAvailable > 0}
                    inventoryIsFetching={props.inventoryIsFetching}
                />
            ) : null}

            {openRegisterDrawer && isLicensee ? (
                <RegisterPartnerParticipantDrawer
                    open={true}
                    onClose={() => setOpenRegisterDrawer(false)}
                    session={props.session}
                    showSnackbar={(message: string, severity: AlertColor) => {
                        setSnackbarMessage(message);
                        setSnackbarSeverity(severity);
                        setShowSnackbar(true);
                    }}
                />
            ) : null}

            {openTransferDrawer ? (
                <TransferParticipantDrawer
                    open={true}
                    participant={selectedParticipant!}
                    fromSession={props.session}
                    onClose={() => setOpenTransferDrawer(false)}
                    showSnackbar={(message: string, severity: AlertColor) => {
                        setSnackbarMessage(message);
                        setSnackbarSeverity(severity);
                        setShowSnackbar(true);
                    }}
                />
            ) : null}

            {openContactDrawer && selectedParticipant ? (
                <ParticipantContactDrawer
                    open={openContactDrawer}
                    onClose={() => setOpenContactDrawer(false)}
                    esiKey={selectedParticipant?.esiKey!}
                />
            ) : null}

            {openDatesDrawer && selectedParticipant ? (
                <ParticipantKeyDatesDrawer
                    open={openDatesDrawer}
                    onClose={() => setOpenDatesDrawer(false)}
                    participant={selectedParticipant}
                    showSnackbar={ShowSnackbarElement}
                    onSaved={props.onParticipantDatesSaved}
                />
            ) : null}

            {openInventoryDrawer ? (
                <InventoryDrawer
                    open={true}
                    esKey={props.session.sessionKey}
                    onClose={() => setOpenInventoryDrawer(false)}
                    showSnackbar={(message: string, severity: AlertColor) => {
                        setSnackbarMessage(message);
                        setSnackbarSeverity(severity);
                        setShowSnackbar(true);
                    }}
                />
            ) : null}

            <CclPersistentDataGrid
                columns={addCustomDataGridFilters(actualColumns as GridColumns<any>)}
                data={viewableParticipants}
                loading={props.participantsRefetching ?? false}
                stateSelector={stateSelector}
                userPreferenceId={0}
                getRowIdFn={(row: Participant) => row.esiKey}
                actionCreator={
                    props.tableStateActionCreator ?? updateSessionsParticipantDatesGridState
                }
                hideSettingsButtons={true}
                exportName={exportFileName}
                miscButtons={props.hideActions ? [showOnlyConfirmedButton] : buttons}
                showCheckboxes={!props.hideActions}
                disableSelectionOnClick
                onSelectedRowsChange={handleSelectedRows}
                initState={JSON.stringify({
                    sorting: {
                        sortModel: [{ field: "name", sort: "asc" }],
                    },
                    columns: {
                        columnVisibilityModel: visibilityModel,
                    },
                })}
                columnVisibility={visibilityModel}
                hideFooter={false}
                autoHeight={props.autoHeight}
            />
        </React.Fragment>
    );
};

export default SessionParticipantDatesListDataGrid;
